import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './EmployeePage.css';
import './Budget.css';

const fmt = (v) => {
  if (v === 0) return <span className="budget-zero">—</span>;
  const neg = v < 0;
  const s = `$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return neg ? <span className="budget-neg">({s})</span> : s;
};

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

const SECTIONS = [
  {
    label: 'REVENUE',
    rows: [
      { key: 'wholesale',      label: 'Wholesale' },
      { key: 'website',        label: 'Website' },
      { key: 'shipping',       label: 'Shipping' },
      { key: 'farmers_market', label: 'Farmers Market' },
      { key: 'discounts',      label: 'Discounts' },
      { key: 'gross_rev',      label: 'Gross Revenue', cls: 'budget-subtotal' },
    ],
  },
  {
    label: 'COST OF GOODS SOLD',
    rows: [
      { key: 'grain',        label: 'Grain' },
      { key: 'bags',         label: 'Bags' },
      { key: 'boxes',        label: 'Boxes' },
      { key: 'total_cogs',   label: 'Total COGS',    cls: 'budget-subtotal' },
      { key: 'gross_profit', label: 'Gross Profit',  cls: 'budget-gross-profit' },
    ],
  },
  {
    label: 'OPERATING EXPENSES',
    rows: [
      { key: 'contractors',         label: 'Contractors' },
      { key: 'g_and_a',             label: 'G&A' },
      { key: 'operating',           label: 'Operating' },
      { key: 'payroll',             label: 'Payroll' },
      { key: 'rent',                label: 'Rent' },
      { key: 'sifter_installation', label: 'Sifter Installation' },
      { key: 'one_time',            label: 'One-Time Expenses' },
      { key: 'total_exp',           label: 'Total Expenses', cls: 'budget-subtotal' },
    ],
  },
];

const Budget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('employee/budget/')
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load budget data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="employee-page"><Loading /></div>;
  if (error)   return <div className="employee-page"><p className="budget-error">{error}</p></div>;

  const { months, is_actual, budget, actuals_2025, notes, generated } = data;

  return (
    <div className="employee-page budget-page">
      <div className="employee-page__header">
        <div>
          <p className="employee-page__eyebrow">Finance</p>
          <h1 className="employee-page__title">2026 Budget</h1>
        </div>
        <p className="budget-generated">Generated {generated} · Cash basis · Depreciation excluded</p>
      </div>

      <div className="budget-scroll">
        <table className="budget-table">
          <thead>
            <tr>
              <th className="budget-th budget-th--label"></th>
              {months.map((m, i) => (
                <th
                  key={m}
                  colSpan={2}
                  className={`budget-th ${is_actual[i] ? 'budget-th--actual' : 'budget-th--proj'}`}
                >
                  {m}
                  <span className="budget-badge">
                    {is_actual[i] ? 'A' : 'B'}
                  </span>
                </th>
              ))}
              <th colSpan={2} className="budget-th budget-th--total">TOTAL</th>
            </tr>
            <tr className="budget-subhdr">
              <td></td>
              {months.map((m) => (
                <React.Fragment key={m}>
                  <td className="budget-sub budget-sub--26">'26</td>
                  <td className="budget-sub budget-sub--25">'25</td>
                </React.Fragment>
              ))}
              <td className="budget-sub budget-sub--26">'26</td>
              <td className="budget-sub budget-sub--25">'25</td>
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((section) => (
              <React.Fragment key={section.label}>
                <tr className="budget-section">
                  <td colSpan={27}>{section.label}</td>
                </tr>
                {section.rows.map(({ key, label, cls = '' }) => (
                  <tr key={key} className={cls}>
                    <td className="budget-label">{label}</td>
                    {months.map((m, i) => (
                      <React.Fragment key={m}>
                        <td className={`budget-cell ${is_actual[i] ? 'budget-cell--actual' : 'budget-cell--proj'} ${cls}`}>
                          {fmt(budget[key][i])}
                        </td>
                        <td className={`budget-cell budget-cell--25 ${cls}`}>
                          {fmt(actuals_2025[key][i])}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className={`budget-cell budget-total--26 ${cls}`}>{fmt(sum(budget[key]))}</td>
                    <td className={`budget-cell budget-total--25 ${cls}`}>{fmt(sum(actuals_2025[key]))}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr className="budget-spacer"><td colSpan={27}></td></tr>
            <tr className="budget-net-income">
              <td className="budget-label">NET INCOME</td>
              {months.map((m, i) => (
                <React.Fragment key={m}>
                  <td className={`budget-cell ${is_actual[i] ? 'budget-cell--actual' : 'budget-cell--proj'} budget-net-income`}>
                    {fmt(budget.net_income[i])}
                  </td>
                  <td className="budget-cell budget-cell--25 budget-net-income">
                    {fmt(actuals_2025.net_income[i])}
                  </td>
                </React.Fragment>
              ))}
              <td className="budget-cell budget-total--26 budget-net-income">{fmt(sum(budget.net_income))}</td>
              <td className="budget-cell budget-total--25 budget-net-income">{fmt(sum(actuals_2025.net_income))}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="budget-notes">
        <p className="budget-notes__legend">
          <span className="budget-notes__badge budget-notes__badge--actual">A</span> Actual per QuickBooks &nbsp;
          <span className="budget-notes__badge budget-notes__badge--proj">B</span> Projected
        </p>
        <ul className="budget-notes__list">
          {notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
    </div>
  );
};

export default Budget;
